/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, ClipboardList, BarChart2,
  RefreshCw, Search, Menu, Eye, ArrowLeft,
} from "lucide-react";
import ReportView from "./ReportView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/panel`;

const createApi = (token) => {
  const h = { Authorization: `Bearer ${token}` };
  return {
    getStats:      () => axios.get(`${API}/stats`,      { headers: h }),
    getOperazioni: () => axios.get(`${API}/operazioni`, { headers: h }),
    getReport:     (p = {}) => axios.get(`${API}/report`, { params: { ...(p.da ? { da: p.da } : {}), ...(p.a ? { a: p.a } : {}) }, headers: h }),
  };
};

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color = "orange" }) => {
  const colors = {
    orange: "border-orange-500 text-orange-400",
    green:  "border-green-500 text-green-400",
    red:    "border-red-500 text-red-400",
    blue:   "border-blue-500 text-blue-400",
    purple: "border-purple-500 text-purple-400",
  };
  return (
    <div className={`tactical-card p-4 border-l-4 ${colors[color]}`}>
      <div className="text-2xl font-bold text-white">{value ?? "–"}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
};

// ── Stato Badge ────────────────────────────────────────────────────────────────
const StatoBadge = ({ attiva }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
    attiva
      ? "bg-green-500/20 text-green-400 border border-green-500/40"
      : "bg-red-500/20 text-red-400 border border-red-500/40"
  }`}>
    {attiva ? "ATTIVA" : "CHIUSA"}
  </span>
);

// ── Read-Only Banner ───────────────────────────────────────────────────────────
const ReadOnlyBanner = () => (
  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm mb-4">
    <Eye size={14} className="flex-shrink-0" />
    Visualizzazione in sola lettura
  </div>
);

// ── Dashboard Tab ──────────────────────────────────────────────────────────────
const DashboardTab = ({ api }) => {
  const [stats, setStats]   = useState(null);
  const [ops,   setOps]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: o }] = await Promise.all([
        api.getStats(),
        api.getOperazioni(),
      ]);
      setStats(s);
      setOps(o);
    } catch {
      toast.error("Errore caricamento dati dashboard");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">Panoramica</h2>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Operazioni Totali"  value={stats?.operazioni_totali}   color="orange" />
        <StatCard label="Operazioni Attive"  value={stats?.operazioni_attive}   color="green"  />
        <StatCard label="Operazioni Chiuse"  value={stats?.operazioni_chiuse}   color="red"    />
        <StatCard label="Soccorritori"        value={stats?.soccorritori_totali} color="blue"   />
        <StatCard label="Missioni"            value={stats?.missioni_totali}     color="purple" />
      </div>

      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
        Operazioni recenti
      </h3>
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              {["Nome", "Località", "Stato", "Data apertura", "Soccorritori"].map(h => (
                <th key={h} className="text-left px-3 py-2 text-slate-400 font-medium text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ops.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Nessuna operazione</td></tr>
            )}
            {ops.slice(0, 10).map(op => (
              <tr key={op.id} className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors">
                <td className="px-3 py-2 text-white font-medium">{op.nome}</td>
                <td className="px-3 py-2 text-slate-300">{op.localita ?? "–"}</td>
                <td className="px-3 py-2"><StatoBadge attiva={op.attiva} /></td>
                <td className="px-3 py-2 text-slate-400 text-xs">{op.data_inizio ? new Date(op.data_inizio).toLocaleDateString("it-IT") : "–"}</td>
                <td className="px-3 py-2 text-slate-300">{op.num_soccorritori ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Interventi Tab ─────────────────────────────────────────────────────────────
const InterventiTab = ({ api }) => {
  const [ops,     setOps]     = useState([]);
  const [query,   setQuery]   = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getOperazioni();
      setOps(data);
    } catch {
      toast.error("Errore caricamento interventi");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = ops.filter(op =>
    [op.nome, op.localita, op.regione, op.comando_vvf]
      .some(v => v?.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div>
      <ReadOnlyBanner />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
            placeholder="Cerca per nome, località, regione..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              {["Nome", "Località", "Regione", "Comando VVF", "Stato", "Soccorritori", "Missioni", "Data apertura"].map(h => (
                <th key={h} className="text-left px-3 py-2 text-slate-400 font-medium text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                {loading ? "Caricamento..." : "Nessun risultato"}
              </td></tr>
            )}
            {filtered.map(op => (
              <tr key={op.id} className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors">
                <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{op.nome}</td>
                <td className="px-3 py-2 text-slate-300">{op.localita ?? "–"}</td>
                <td className="px-3 py-2 text-slate-300">{op.regione ?? "–"}</td>
                <td className="px-3 py-2 text-slate-300">{op.comando_vvf ?? "–"}</td>
                <td className="px-3 py-2"><StatoBadge attiva={op.attiva} /></td>
                <td className="px-3 py-2 text-slate-300 text-center">{op.num_soccorritori ?? 0}</td>
                <td className="px-3 py-2 text-slate-300 text-center">{op.num_missioni ?? 0}</td>
                <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap">
                  {op.data_inizio ? new Date(op.data_inizio).toLocaleDateString("it-IT") : "–"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-600 mt-2">{filtered.length} risultat{filtered.length === 1 ? "o" : "i"}</p>
    </div>
  );
};

// ── Report Tab ─────────────────────────────────────────────────────────────────
// Grafici e layout del report: spostati nel componente condiviso ReportView.
const ReportTab = ({ api, scopeType, logoBase64 }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (periodo) => {
    setLoading(true);
    try {
      const { data: d } = await api.getReport(periodo);
      setData(d);
    } catch {
      toast.error("Errore caricamento report");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Solo al PRIMO caricamento: sostituire il report a ogni refetch smonterebbe
  // ReportView e con esso lo stato del filtro (le date selezionate sparivano).
  if (loading && !data) return <div className="text-slate-400 text-sm text-center py-20">Caricamento report…</div>;
  if (!data)   return <div className="text-red-400 text-sm text-center py-20">Errore nel caricamento dei dati.</div>;

  const isNational = scopeType === "national";
  const scopeLabel = scopeType === "national" ? "Nazionale" : scopeType === "comando" ? "Provinciale" : "Regionale";
  const scopeDesc = scopeType === "national"
    ? "Riepilogo aggregato di tutte le operazioni nel sistema"
    : scopeType === "comando"
      ? "Dati filtrati per comando provinciale di competenza"
      : "Dati filtrati per regione di competenza";

  return (
    <div className="max-w-5xl mx-auto">
      <ReportView
        data={data}
        title={`Report ${scopeLabel}`}
        subtitle={scopeDesc}
        onRefresh={load}
        loading={loading}
        logoBase64={logoBase64}
        isNational={isNational}
      />
    </div>
  );
};

// ── Main Panel ─────────────────────────────────────────────────────────────────
const DirRegPanel = ({ operatore, token, onBack, logoBase64 }) => {
  const [tab,         setTab]         = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scopeType,   setScopeType]   = useState(null);
  const [scopeValue,  setScopeValue]  = useState(null);

  const api = createApi(token);

  const isCon    = operatore?.ruolo === "con";
  const roleLabel = isCon ? "CON" : "DIR REG";

  // Fetch scope on mount
  useEffect(() => {
    axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setScopeType(data.scope_type);
        setScopeValue(data.scope_value);
      })
      .catch(() => {});
  }, [token]);

  const tabs = [
    { id: "dashboard",  label: "Dashboard",  Icon: LayoutDashboard },
    { id: "operazioni", label: "Interventi",  Icon: ClipboardList   },
    { id: "report",     label: "Report",      Icon: BarChart2        },
  ];

  const SidebarContent = () => (
    <nav className="flex flex-col gap-1 p-3">
      {tabs.map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => { setTab(id); setSidebarOpen(false); }}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
            tab === id
              ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
              : "text-slate-400 hover:bg-slate-800 hover:text-white"
          }`}
        >
          <Icon size={16} />
          {label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header — uniformato allo stile Admin */}
      <header className="bg-slate-900 border-b border-slate-800 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4 flex-shrink-0">
        {/* Hamburger — solo mobile */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-300 hover:bg-slate-800"
          onClick={() => setSidebarOpen(v => !v)}
          aria-label="Apri menu"
        >
          <Menu size={20} />
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>
          <ArrowLeft size={14} /> <span className="hidden sm:inline">App</span>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {logoBase64 && (
            <img src={logoBase64} alt="Logo" className="w-7 h-7 object-contain rounded" />
          )}
          <span className="text-white font-bold text-sm sm:text-base truncate">Pannello {roleLabel}</span>
        </div>
        <div className="ml-auto text-xs text-slate-400 hidden sm:block truncate">{operatore?.email}</div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-48 bg-slate-900 border-r border-slate-800 flex-shrink-0">
          <SidebarContent />
        </aside>

        {/* Mobile sidebar drawer */}
        {sidebarOpen && (
          <>
            <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
            <aside className="fixed left-0 top-0 bottom-0 w-56 bg-slate-900 border-r border-slate-800 z-40 md:hidden flex flex-col pt-16">
              <SidebarContent />
            </aside>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {tab === "dashboard"  && <DashboardTab  api={api} />}
          {tab === "operazioni" && <InterventiTab api={api} />}
          {tab === "report"     && <ReportTab     api={api} scopeType={scopeType} logoBase64={logoBase64} />}
        </main>
      </div>
    </div>
  );
};

export default DirRegPanel;

// ── ReportPage ───────────────────────────────────────────────────────────────
// Wrapper standalone del Report per l'uso come pagina del menu principale
// (ruoli CON / DIR_REG). Riusa ReportTab + l'API panel + il fetch dello scope,
// senza la chrome del pannello dedicato (header/sidebar tabs).
export const ReportPage = ({ token }) => {
  const [scopeType, setScopeType] = useState(null);
  const api = createApi(token);

  useEffect(() => {
    axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setScopeType(data.scope_type))
      .catch(() => {});
  }, [token]);

  return (
    <div className="p-4 md:p-6">
      <ReportTab api={api} scopeType={scopeType} />
    </div>
  );
};
